class PricingCalculatorModule {
  constructor(container) {
    this.container = container;
    this.data = JSON.parse(container.dataset.pricing);

    this.selectWrapper = container.querySelector('.select');
    this.selectTrigger = container.querySelector('.select__value');
    this.selectTriggerSpan = this.selectTrigger.querySelector('span');
    this.selectDropdown = container.querySelector('.select__dropdown');
    this.nativeSelect = container.querySelector('.pricing-calculator__product-select');
    this.options = Array.from(this.selectDropdown.querySelectorAll('[role="option"]'));

    this.participantsInput = container.querySelector('.pricing-calculator__participants-input');
    this.resultsEl = container.querySelector('.pricing-calculator__results');
    this.costPerEl = container.querySelector('.pricing-calculator__cost-per-participant');
    this.totalCostEl = container.querySelector('.pricing-calculator__total-cost');
    this.hintEl = container.querySelector('.pricing-calculator__hint');
    this.fieldWrapper = this.selectWrapper.closest('.field');

    this.init();
  }

  init() {
    this._initSelect();
    this._initParticipantsField();
    this.participantsInput.addEventListener('input', () => this._calculate());
  }

  _initParticipantsField() {
    const wrapper = this.participantsInput.closest('.field');
    if (!wrapper) return;

    // Start active since we have value="0"
    wrapper.classList.add('is-active');

    this.participantsInput.addEventListener('focus', () => {
      wrapper.classList.add('is-focused', 'is-active');
    });

    this.participantsInput.addEventListener('blur', () => {
      wrapper.classList.remove('is-focused');
      this._toggleActive(wrapper, Boolean(this.participantsInput.value));
    });
  }

  /* Custom select — mirrors _form.js pattern (single-select only) */
  _initSelect() {
    const openDropdown = () => {
      this.selectWrapper.classList.add('active');
      this.selectTrigger.setAttribute('aria-expanded', 'true');
      this.selectDropdown.hidden = false;
      this.fieldWrapper.classList.add('is-focused', 'is-active');
    };

    const closeDropdown = () => {
      this.selectWrapper.classList.remove('active');
      this.selectTrigger.setAttribute('aria-expanded', 'false');
      this.selectDropdown.hidden = true;
      this.fieldWrapper.classList.remove('is-focused');
      this._toggleActive(this.fieldWrapper, Boolean(this.nativeSelect.value));
    };

    const toggleDropdown = () => {
      this.selectWrapper.classList.contains('active') ? closeDropdown() : openDropdown();
    };

    this.selectTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleDropdown();
    });

    this.selectTrigger.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          openDropdown();
          this.options[0]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          openDropdown();
          this.options[this.options.length - 1]?.focus();
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          toggleDropdown();
          break;
      }
    });

    this.options.forEach((opt, idx) => {
      opt.setAttribute('tabindex', '-1');

      opt.addEventListener('click', (e) => {
        e.preventDefault();
        this.nativeSelect.value = opt.dataset.value;
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.selectTriggerSpan.textContent = opt.textContent;
        this.options.forEach(o => {
          o.classList.remove('selected');
          o.setAttribute('aria-selected', 'false');
        });
        opt.classList.add('selected');
        opt.setAttribute('aria-selected', 'true');
        closeDropdown();
        this._calculate();
      });

      opt.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            (this.options[idx + 1] || this.options[0]).focus();
            break;
          case 'ArrowUp':
            e.preventDefault();
            (this.options[idx - 1] || this.options[this.options.length - 1]).focus();
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            opt.click();
            break;
          case 'Escape':
            e.preventDefault();
            this.selectTrigger.focus();
            closeDropdown();
            break;
        }
      });
    });

    this._closeDropdown = closeDropdown;
  }

  _toggleActive(wrapper, active) {
    wrapper.classList[active ? 'add' : 'remove']('is-active');
  }

  _calculate() {
    const productName = this.nativeSelect.value;
    const participants = parseInt(this.participantsInput.value, 10);
    const minParticipants = this.data.breakpoints[0];
    const currency = this.data.currency;

    if (!productName || !participants || participants < minParticipants) {
      this.hintEl.hidden = !this.participantsInput.value || !participants || participants < minParticipants ? false : true;
      this.costPerEl.textContent = `${currency} 0.00`;
      this.totalCostEl.textContent = `${currency} 0.00`;
      return;
    }

    this.hintEl.hidden = true;

    const product = this.data.products.find(p => p.name === productName);
    if (!product) return;

    // Find highest breakpoint <= participants
    let priceIndex = 0;
    for (let i = this.data.breakpoints.length - 1; i >= 0; i--) {
      if (this.data.breakpoints[i] <= participants) {
        priceIndex = i;
        break;
      }
    }

    const costPer = product.prices[priceIndex];
    const total = costPer * participants;

    this.costPerEl.textContent = `${currency} ${this._formatNumber(costPer)}`;
    this.totalCostEl.textContent = `${currency} ${this._formatNumber(total)}`;
  }

  _formatNumber(num) {
    return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

// Single global click listener for all pricing calculator selects
document.addEventListener('click', (e) => {
  if (!e.target.closest('.select')) {
    PricingCalculatorModule._instances?.forEach(instance => instance._closeDropdown?.());
  }
});

PricingCalculatorModule._instances = [];

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.pricing-calculator').forEach(el => {
    PricingCalculatorModule._instances.push(new PricingCalculatorModule(el));
  });
});
